import React from 'react';
import {
  Card,
  Pagination,
  PageSection,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Label,
  PaginationVariant
} from '@patternfly/react-core';
import { Table, Thead, Tr, Th, Tbody, Td } from '@patternfly/react-table';
import { assetsRows, assetsColumns } from '@app/Findings/sampleData';
import { ChartDonut } from '@patternfly/react-charts';
import { PRODUCT_NAME } from 'src/config/partner';

export const assets: React.FunctionComponent = () => {
  const [page, setPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(10);
  const [paginatedRows, setPaginatedRows] = React.useState(assetsRows.slice(0, 10));

  const handleSetPage = (
    _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPage: number,
    _perPage: number,
    startIdx: number,
    endIdx: number
  ) => {
    setPaginatedRows(assetsRows.slice(startIdx, endIdx));
    setPage(newPage);
  };

  const handlePerPageSelect = (
    _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPerPage: number,
    newPage: number,
    startIdx: number,
    endIdx: number
  ) => {
    setPaginatedRows(assetsRows.slice(startIdx, endIdx));
    setPage(newPage);
    setPerPage(newPerPage);
  };

  const renderPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={assetsRows.length}
      page={page}
      perPage={perPage}
      onSetPage={handleSetPage}
      onPerPageSelect={handlePerPageSelect}
      variant={variant as PaginationVariant}
      titles={{
        paginationAriaLabel: `${variant} pagination`
      }}
    />
  );

  const tableToolbar = (
    <Toolbar usePageInsets id="compact-toolbar">
      <ToolbarContent>
        <ToolbarItem variant="pagination">{renderPagination('top', true)}</ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  const renderLabel = (labelText: string) => {
    switch (labelText) {
      case 'TRUE':
        return <Label color="green">{labelText}</Label>;
      case 'FALSE':
        return <Label color="red">{labelText}</Label>;
      default:
        return <></>;
    }
  };

  const renderPriority = (priority: number) => {
    if (priority > 8) {
        return <Label color="red">{priority}</Label>;
    } else if (priority > 5) {
        return <Label color="orange">{priority}</Label>;
    } else {
        return <Label color="green">{priority}</Label>;
    }
  };

  const trueCountDefender = assetsRows.filter((obj) => obj.DefenderCoverage === 'TRUE').length;
  const falseCountDefender = assetsRows.filter((obj) => obj.DefenderCoverage === 'FALSE').length;
  const trueCountIntune = assetsRows.filter((obj) => obj.IntuneCoverage === 'TRUE').length;
  const falseCountIntune = assetsRows.filter((obj) => obj.IntuneCoverage === 'FALSE').length;

  return (
    <React.Fragment>
        <PageSection isFilled>
          <Title headingLevel="h1" size="xl">{PRODUCT_NAME} Detected Assets</Title>
          <br />
          <Card>
            {tableToolbar}
            <Table variant="compact" aria-label="Compact Table">
              <Thead>
                <Tr>
                  <Th key={0}>{assetsColumns[0]}</Th>
                  <Th key={1}>{assetsColumns[1]}</Th>
                  <Th key={2}>{assetsColumns[2]}</Th>
                  <Th key={3}>{assetsColumns[3]}</Th>
                  <Th key={4}>{assetsColumns[4]}</Th>
                  <Th key={5}>{assetsColumns[5]}</Th>
                  <Th key={6}>{assetsColumns[6]}</Th>
                  <Th key={6}>{assetsColumns[7]}</Th>
                  <Th key={6}>{assetsColumns[8]}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedRows.map((row, rowIndex) => (
                  <Tr key={rowIndex}>
                    <>
                      <Td dataLabel={assetsColumns[0]}>{row.Source}</Td>
                      <Td dataLabel={assetsColumns[1]}>{row.Hostname}</Td>
                      <Td dataLabel={assetsColumns[2]}>{row.IPAddress}</Td>
                      <Td dataLabel={assetsColumns[3]}>{row.OSType}</Td>
                      <Td dataLabel={assetsColumns[4]}>{row.OSName}</Td>
                      <Td dataLabel={assetsColumns[5]}>{row.OSVersion}</Td>
                      <Td dataLabel={assetsColumns[6]}>{row.LastSeen}</Td>
                      <Td dataLabel={assetsColumns[7]}>{renderLabel(row.DefenderCoverage)}</Td>
                      <Td dataLabel={assetsColumns[8]}>{renderLabel(row.IntuneCoverage)}</Td>
                    </>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Card>
        </PageSection>

        <div style={{ display: 'flex', justifyContent: 'space-around' }}>
        <div style={{ height: '230px', width: '450px' }}>
          <ChartDonut
            ariaDesc="Assets covered by Defender"
            ariaTitle="Assets covered by Defender"
            data={[{ x: 'Assets NOT covered by Defender', y: falseCountDefender }, { x: 'Assets covered by Defender', y: trueCountDefender }]}
            labels={({ datum }) => `${datum.x}: ${datum.y}`}
            style={{
                data: {
                    fill: ({ datum }) => {
                        if (datum.x === 'Assets NOT covered by Defender') {
                            return 'red';
                        } else if (datum.x === 'Assets covered by Defender') {
                            return 'green';
                        }
                        return null;
                    }
                }
            }}
            legendData={[{ name: 'Unprotected Assets (Defender): '+falseCountDefender, symbol: { fill: 'red' }}, { name: 'Protected Assets (Defender): '+trueCountDefender, symbol: { fill: 'green' }}]}
            legendOrientation="vertical"
            legendPosition="right"
            name="chart1"
             padding={{
               bottom: 20,
               left: 0,
               right: 250, // Adjusted to accommodate legend
               top: 20
             }}
            title={assetsRows.length}
            subTitle="Detected Assets"
            width={450}
            innerRadius={80}
            constrainToVisibleArea
          />
        </div>
        <div style={{ height: '230px', width: '450px' }}>
          <ChartDonut
            ariaDesc="Assets covered by Intune"
            ariaTitle="Assets covered by Intune"
            data={[{ x: 'Assets NOT covered by Intune', y: falseCountIntune }, { x: 'Assets covered by Intune', y: trueCountIntune }]}
            labels={({ datum }) => `${datum.x}: ${datum.y}`}
            style={{
                data: {
                    fill: ({ datum }) => {
                        if (datum.x === 'Assets NOT covered by Intune') {
                            return 'red';
                        } else if (datum.x === 'Assets covered by Intune') {
                            return 'green';
                        }
                        return null;
                    }
                }
            }}
            legendData={[{ name: 'Unprotected Assets (Intune): '+falseCountIntune, symbol: { fill: 'red' }}, { name: 'Protected Assets (Intune): '+trueCountIntune, symbol: { fill: 'green' }}]}
            legendOrientation="vertical"
            legendPosition="right"
            name="chart2"
             padding={{
               bottom: 20,
               left: 0,
               right: 250, // Adjusted to accommodate legend
               top: 20
             }}
            title={assetsRows.length}
            subTitle="Detected Assets"
            width={450}
            innerRadius={80}
            constrainToVisibleArea
          />
        </div>
        </div>
    </React.Fragment>
  );
};
